import {
    afterEveryRender, Component, computed, DestroyRef, ElementRef, EventEmitter, inject, Input, OnInit, Output, signal, ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { StepperMessage } from '../../../common/enums/message.enum';
import { FlowContext } from '../flows/flow.types';
import { OrderStepperService, STEPPER_RING_RADIUS } from '../flows/order-stepper.service';
import { StepperChildView, StepperSectionView, StepperStatus, StepperView } from '../flows/stepper.types';

/**
 * Sticky product-flow stepper (story 10869) shown above the order page. Sections that do not fit the
 * available width fold into a "+N more" pill; the Go To dialog lists every section and page.
 */
@Component({
    selector: 'app-order-stepper',
    standalone: false,
    templateUrl: './order-stepper.component.html',
    styleUrl: './order-stepper.component.scss'
})
export class OrderStepperComponent implements OnInit {
    /** Order context used to evaluate which pages belong to the agent's path (the order layout). */
    @Input({ required: true }) set ctx(value: FlowContext) {
        this.context.set(value);
    }

    /** Emits the route of a reached page the agent picked. */
    @Output() stepSelected = new EventEmitter<string>();

    @ViewChild('bar') barRef?: ElementRef<HTMLElement>;
    @ViewChild('measure') measureRef?: ElementRef<HTMLElement>;

    readonly StepperMessage = StepperMessage;
    readonly StepperStatus = StepperStatus;
    readonly ringRadius = STEPPER_RING_RADIUS;

    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private stepperService = inject(OrderStepperService);

    private context = signal<FlowContext | null>(null);
    private url = signal(this.router.url);

    readonly view = computed<StepperView | null>(() => {
        const ctx = this.context();
        return ctx ? this.stepperService.build(ctx, this.url()) : null;
    });

    /** Whether finished sections hide their "x of y" count (first space saving, with 2+ finished sections). */
    readonly compactDone = signal(false);

    /** Whether the finished leading sections are folded into the "N done" pill (only when the row does not fit). */
    readonly grouped = signal(false);

    /** Sections shown one by one, after the "N done" pill when it is used. */
    readonly barSections = computed(() => {
        const view = this.view();
        if (!view) {
            return [];
        }
        return this.grouped() ? view.sections.slice(view.groupedCount) : view.sections;
    });

    readonly fitCount = signal(Number.MAX_SAFE_INTEGER);
    readonly shownSections = computed(() => this.barSections().slice(0, this.fitCount()));
    readonly hiddenCount = computed(() => Math.max(this.barSections().length - this.fitCount(), 0));

    readonly dialogVisible = signal(false);
    readonly expanded = signal<ReadonlySet<string>>(new Set());

    constructor() {
        afterEveryRender(() => this.measure());
    }

    ngOnInit(): void {
        this.recordArrival();
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(event => {
            this.url.set(event.urlAfterRedirects);
            this.recordArrival();
            this.dialogVisible.set(false);
        });
    }

    /** Any step opens the Go To dialog (a finished single-page step goes straight to its page). */
    onSectionClick(section: StepperSectionView): void {
        if (this.view()?.locked) {
            return;
        }
        if (section.status === StepperStatus.Completed && section.total === 1) {
            this.goTo(section.children[0]);
            return;
        }
        this.openDialog(section.id);
    }

    onDialogSectionClick(section: StepperSectionView): void {
        if (this.isSectionDisabled(section)) {
            return;
        }
        if (section.total === 1) {
            this.goTo(section.children[0]);
            return;
        }
        this.toggle(section.id);
    }

    openDialog(expandId?: string): void {
        this.expanded.set(new Set(expandId ? [expandId] : []));
        this.dialogVisible.set(true);
    }

    goTo(child: StepperChildView): void {
        if (child.status === StepperStatus.NotStarted || this.view()?.locked) {
            return;
        }
        this.dialogVisible.set(false);
        if (!child.current) {
            this.stepSelected.emit(child.route);
        }
    }

    isSectionDisabled(section: StepperSectionView): boolean {
        return section.status === StepperStatus.NotStarted || !!this.view()?.locked;
    }

    isExpanded(id: string): boolean {
        return this.expanded().has(id);
    }

    private toggle(id: string): void {
        const next = new Set(this.expanded());
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        this.expanded.set(next);
    }

    private recordArrival(): void {
        const ctx = this.context();
        if (ctx) {
            this.stepperService.recordArrival(ctx, this.url());
        }
    }

    /**
     * Fits sections to the bar width using the hidden measuring row, which renders every section (with counts)
     * and the done pill. When the full row does not fit: finished sections drop their "x of y" count (with 2+
     * finished), then the finished leading sections fold into the "N done" pill, then sections that still do
     * not fit go into "+N more". The current section always shows.
     */
    private measure(): void {
        const bar = this.barRef?.nativeElement;
        const measure = this.measureRef?.nativeElement;
        const view = this.view();
        if (!bar || !measure || !view) {
            return;
        }
        const barStyle = getComputedStyle(bar);
        const gap = parseFloat(barStyle.columnGap) || 0;
        // Each element in the row takes its width plus one gap.
        const widthOf = (selector: string) => Array.from(measure.querySelectorAll<HTMLElement>(selector))
            .reduce((sum, el) => sum + el.offsetWidth + gap, 0);
        const itemEls = Array.from(measure.querySelectorAll<HTMLElement>('[data-stepper-item]'));
        const items = itemEls.map(el => el.offsetWidth + gap);
        // Width of each item once a finished section's count (stacked under its label) is hidden.
        const compactItems = itemEls.map((el, i) => {
            const count = el.querySelector<HTMLElement>('[data-stepper-done-count]');
            const label = el.querySelector<HTMLElement>('[data-stepper-label]');
            return items[i] - (count && label ? Math.max(count.offsetWidth - label.offsetWidth, 0) : 0);
        });
        const available = bar.clientWidth - parseFloat(barStyle.paddingLeft) - parseFloat(barStyle.paddingRight)
            - widthOf('[data-stepper-fixed]') + gap;
        const sum = (widths: number[]) => widths.reduce((total, width) => total + width, 0);

        const finished = view.sections.filter(s => s.status === StepperStatus.Completed && s.total > 1).length;
        const compact = sum(items) > available && view.completedCount >= 2 && finished > 0;
        const widths = compact ? compactItems : items;
        const grouped = sum(widths) > available && view.groupedCount > 0;
        const candidates = grouped ? widths.slice(view.groupedCount) : widths;
        const room = grouped ? available - widthOf('[data-stepper-group]') : available;

        let fit = candidates.length;
        if (sum(candidates) > room) {
            const morePill = widthOf('[data-stepper-more]');
            let used = 0;
            fit = 0;
            while (fit < candidates.length && used + candidates[fit] <= room - morePill) {
                used += candidates[fit++];
            }
        }
        const shown = grouped ? view.sections.slice(view.groupedCount) : view.sections;
        fit = Math.max(fit, shown.findIndex(s => s.current) + 1, 1);
        if (compact !== this.compactDone()) {
            this.compactDone.set(compact);
        }
        if (grouped !== this.grouped()) {
            this.grouped.set(grouped);
        }
        if (fit !== this.fitCount()) {
            this.fitCount.set(fit);
        }
    }
}
